import type {
  ShippingMessagingContext,
  ShippingMethodId,
  ShippingSettings,
  ShippingVars,
} from "./types";
import { getMarketTransitWindow } from "./messaging";
import { resolveShippingMethod } from "./shippingFee";

export function getHeroTrustLineKey(countryCode?: string): string {
  const code = countryCode?.toUpperCase();
  if (code === "US") return "trust_line_US";
  if (code === "UK" || code === "GB") return "trust_line_UK";
  return "trust_line";
}

export function buildShippingVars(
  settings: ShippingSettings,
  ctx: ShippingMessagingContext = {},
): ShippingVars {
  const standard = resolveShippingMethod(settings, "standard");
  const express = resolveShippingMethod(settings, "express");
  const marketTransit = getMarketTransitWindow(settings, ctx.countryCode);

  return {
    returnDays: settings.returnWindowDays,
    processingDaysMin: settings.processingDaysMin,
    processingDaysMax: settings.processingDaysMax,
    standardTransitMin: standard?.transitDaysMin ?? 5,
    standardTransitMax: standard?.transitDaysMax ?? 12,
    expressTransitMin: express?.transitDaysMin ?? 3,
    expressTransitMax: express?.transitDaysMax ?? 7,
    marketTransitMin: marketTransit.min,
    marketTransitMax: marketTransit.max,
    expressFee: express?.fee ?? 0,
    expressEnabled: express?.enabled ?? false,
  };
}

/** Values safe for next-intl interpolation (no booleans). */
export function toTransitTemplateVars(vars: ShippingVars): Record<string, string | number> {
  return {
    returnDays: vars.returnDays,
    processingDaysMin: vars.processingDaysMin,
    processingDaysMax: vars.processingDaysMax,
    standardTransitMin: vars.standardTransitMin,
    standardTransitMax: vars.standardTransitMax,
    expressTransitMin: vars.expressTransitMin,
    expressTransitMax: vars.expressTransitMax,
    marketTransitMin: vars.marketTransitMin,
    marketTransitMax: vars.marketTransitMax,
    transitMin: vars.standardTransitMin,
    transitMax: vars.standardTransitMax,
    expressFee: vars.expressFee,
  };
}

export function interpolateShippingTemplate(
  template: string,
  vars: Record<string, string | number>,
): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(vars[key] ?? ""));
}

export function getCheckoutMethodVars(
  settings: ShippingSettings,
  methodId: ShippingMethodId,
  expressFeeFormatted?: string,
): Record<string, string | number> {
  const shippingVars = buildShippingVars(settings, { methodId });
  const vars = toTransitTemplateVars(shippingVars);
  if (methodId === "express") {
    return {
      ...vars,
      transitMin: shippingVars.expressTransitMin,
      transitMax: shippingVars.expressTransitMax,
      expressFee: expressFeeFormatted ?? String(shippingVars.expressFee),
    };
  }
  return {
    ...vars,
    transitMin: shippingVars.standardTransitMin,
    transitMax: shippingVars.standardTransitMax,
  };
}
